/*
 * Copyright 2017 Palantir Technologies, Inc. All rights reserved.
 * Licensed under the BSD-3 License as modified (the “License”); you may obtain a copy
 * of the license at https://github.com/palantir/blueprint/blob/master/LICENSE
 * and https://github.com/palantir/blueprint/blob/master/PATENTS
 */
"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var classNames = require("classnames");
var PureRender = require("pure-render-decorator");
var React = require("react");
var core_1 = require("@blueprintjs/core");
var Classes = require("../../common/classes");
/** special value for absence of active tag */
var NONE = -1;
var TagInput = (function (_super) {
    tslib_1.__extends(TagInput, _super);
    function TagInput() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.state = {
            activeIndex: NONE,
            inputValue: "",
            isInputFocused: false,
        };
        _this.refHandlers = {
            input: function (ref) {
                _this.inputElement = ref;
                // can't use safeInvoke cuz inputProps.ref can be `string | function`
                var refHandler = _this.props.inputProps.ref;
                if (core_1.Utils.isFunction(refHandler)) {
                    refHandler(ref);
                }
            },
        };
        _this.renderTag = function (tag, index) {
            var tagProps = _this.props.tagProps;
            var props = core_1.Utils.isFunction(tagProps) ? tagProps(tag, index) : tagProps;
            return (React.createElement(core_1.Tag, tslib_1.__assign({ active: index === _this.state.activeIndex, "data-tag-index": index, key: tag + "__" + index, onRemove: _this.handleRemoveTag }, props), tag));
        };
        _this.handleContainerClick = function () {
            if (_this.inputElement != null) {
                _this.inputElement.focus();
            }
        };
        _this.handleBlur = function () { return requestAnimationFrame(function () {
            // this event is attached to the container element to capture all blur events from inside.
            // we only need to "unfocus" if the blur event is leaving the container.
            // defer this check using rAF so activeElement will have updated.
            if (_this.inputElement != null && !_this.inputElement.parentElement.contains(document.activeElement)) {
                _this.setState({ activeIndex: NONE, isInputFocused: false });
            }
        }); };
        _this.handleInputFocus = function (event) {
            _this.setState({ isInputFocused: true });
            core_1.Utils.safeInvoke(_this.props.inputProps.onFocus, event);
        };
        _this.handleInputChange = function (event) {
            _this.setState({ activeIndex: NONE, inputValue: event.currentTarget.value });
            core_1.Utils.safeInvoke(_this.props.inputProps.onChange, event);
        };
        _this.handleInputKeyDown = function (event) {
            var _a = event.currentTarget, selectionEnd = _a.selectionEnd, value = _a.value;
            if (event.which === core_1.Keys.ENTER && value.length > 0) {
                // enter key on non-empty string invokes onAdd
                var shouldClearInput = core_1.Utils.safeInvoke(_this.props.onAdd, value);
                // only explicit return false cancels text clearing
                if (shouldClearInput !== false) {
                    _this.setState({ inputValue: "" });
                }
            }
            else if (selectionEnd === 0 && _this.props.values.length > 0) {
                // cursor at beginning of input allows interaction with tags.
                // use selectionEnd to verify cursor position and no text selection.
                if (event.which === core_1.Keys.ARROW_LEFT || event.which === core_1.Keys.ARROW_RIGHT) {
                    var nextIndex = _this.getNextActiveIndex(event.which === core_1.Keys.ARROW_RIGHT ? 1 : -1);
                    if (nextIndex !== _this.state.activeIndex) {
                        event.preventDefault();
                        _this.setState({ activeIndex: nextIndex });
                    }
                }
                else if (event.which === core_1.Keys.BACKSPACE) {
                    _this.removeActiveIndex(event);
                }
            }
            core_1.Utils.safeInvoke(_this.props.inputProps.onKeyDown, event);
        };
        _this.handleRemoveTag = function (event) {
            // using data attribute to simplify callback logic -- one handler for all children
            var index = +event.currentTarget.parentElement.getAttribute("data-tag-index");
            core_1.Utils.safeInvoke(_this.props.onRemove, _this.props.values[index], index);
        };
        return _this;
    }
    TagInput.prototype.render = function () {
        var _a = this.props, className = _a.className, inputProps = _a.inputProps, values = _a.values;
        var classes = classNames(core_1.Classes.INPUT, Classes.TAG_INPUT, (_b = {},
            _b[core_1.Classes.ACTIVE] = this.state.isInputFocused,
            _b), className);
        return (React.createElement("div", { className: classes, onBlur: this.handleBlur, onClick: this.handleContainerClick },
            values.map(this.renderTag),
            React.createElement("input", tslib_1.__assign({ value: this.state.inputValue }, inputProps, { onFocus: this.handleInputFocus, onChange: this.handleInputChange, onKeyDown: this.handleInputKeyDown, ref: this.refHandlers.input, className: classNames(Classes.INPUT_GHOST, inputProps.className) }))));
        var _b;
    };
    TagInput.prototype.getNextActiveIndex = function (direction) {
        var activeIndex = this.state.activeIndex;
        var totalItems = this.props.values.length;
        if (activeIndex === NONE && direction < 0) {
            // nothing active, moving left: select last
            return totalItems - 1;
        }
        else if (activeIndex === NONE && direction > 0) {
            // nothing active, moving right: do nothing
            return NONE;
        }
        else {
            // otherwise, move in direction and clamp to bounds.
            // note that upper bound allows going one beyond last item
            // so focus can move off the right end, into the text input.
            return core_1.Utils.clamp(activeIndex + direction, 0, totalItems);
        }
    };
    TagInput.prototype.removeActiveIndex = function (event) {
        var previousActiveIndex = this.state.activeIndex;
        // always move leftward one item (this will focus last item if nothing is focused)
        this.setState({ activeIndex: this.getNextActiveIndex(-1) });
        // delete item if there was a previous valid selection (ignore first backspace to focus last item)
        if (previousActiveIndex !== NONE && previousActiveIndex < this.props.values.length) {
            event.preventDefault();
            core_1.Utils.safeInvoke(this.props.onRemove, this.props.values[previousActiveIndex], previousActiveIndex);
        }
    };
    return TagInput;
}(core_1.AbstractComponent));
TagInput.displayName = "Blueprint.TagInput";
TagInput.defaultProps = {
    inputProps: {},
    tagProps: {},
};
TagInput = tslib_1.__decorate([
    PureRender
], TagInput);
exports.TagInput = TagInput;

//# sourceMappingURL=tagInput.js.map



//////////////////
// WEBPACK FOOTER
// ./node_modules/@blueprintjs/labs/dist/components/tag-input/tagInput.js
// module id = 2z/C
// module chunks = 1